import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { downloadCSV } from '@/lib/csv';
import type { PaymentAttemptRow } from '@/lib/paymentAttempts';

// ─────────────────────────────────────────────────────────────
// Journal des paiements — chaque tentative échouée d'un fournisseur
// (FeexPay en priorité) avec montant, opérateur, statut HTTP et réponse
// brute. Sert au diagnostic : on voit exactement ce que la passerelle a
// répondu, sans avoir à reproduire le paiement.
// ─────────────────────────────────────────────────────────────

type Period = '24h' | '7d' | '30d' | 'all';

const PERIOD_LABELS: Record<Period, string> = {
  '24h': '24 heures',
  '7d': '7 jours',
  '30d': '30 jours',
  all: 'Tout',
};

const PROVIDER_LABELS: Record<string, string> = {
  feexpay: 'FeexPay',
  stripe: 'Stripe',
  paypal: 'PayPal',
  toss: 'Toss',
  square: 'Square',
  cmi: 'CMI',
  wave: 'Wave',
};

const FLOW_LABELS: Record<string, string> = {
  subscription: 'Abonnement',
  shop_order: 'Commande boutique',
  other: 'Autre',
};

const STATUS_STYLE: Record<string, string> = {
  failed: 'bg-red-50 text-red-600',
  declined: 'bg-amber-50 text-amber-700',
  error: 'bg-red-100 text-red-700',
  initiated: 'bg-primary-100 text-primary-700',
  confirmed: 'bg-accent-100 text-accent-700',
  unmatched: 'bg-secondary-100 text-secondary-800',
};

const STATUS_LABELS: Record<string, string> = {
  failed: 'Échec',
  declined: 'Refusé',
  error: 'Erreur',
  initiated: 'Envoyée à FeexPay',
  confirmed: 'Confirmée',
  unmatched: 'Webhook non apparié',
};

const STATUS_ICONS: Record<string, string> = {
  failed: 'ri-error-warning-line',
  declined: 'ri-close-circle-line',
  error: 'ri-error-warning-line',
  initiated: 'ri-send-plane-line',
  confirmed: 'ri-checkbox-circle-line',
  unmatched: 'ri-question-line',
};

/** Statuts considérés comme un échec (pour les compteurs et le graphique). */
const FAILED_STATES = ['failed', 'declined', 'error'];
const isFailure = (status: string) => FAILED_STATES.includes(status);

function sinceISO(period: Period): string | null {
  if (period === 'all') return null;
  const hours = period === '24h' ? 24 : period === '7d' ? 24 * 7 : 24 * 30;
  return new Date(Date.now() - hours * 3600 * 1000).toISOString();
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function relative(iso: string): string {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `il y a ${hours} h`;
  return `il y a ${Math.round(hours / 24)} j`;
}

function amountLabel(value: number | null, currency: string | null): string {
  if (value === null || value === undefined) return '—';
  return `${Number(value).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${currency || ''}`.trim();
}

function prettyEndpoint(endpoint: string | null): string {
  if (!endpoint) return '—';
  try {
    const url = new URL(endpoint);
    return `${url.pathname}`;
  } catch {
    return endpoint;
  }
}

export default function PaymentAttemptsTab() {
  const [searchParams] = useSearchParams();
  // Lien direct depuis la pastille SuperAdmin : ?attempt=<id>
  const targetAttempt = Number(searchParams.get('attempt') || 0) || null;

  const [rows, setRows] = useState<PaymentAttemptRow[]>([]);
  const [weekRows, setWeekRows] = useState<{ operator: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>(targetAttempt ? 'all' : '7d');
  const [provider, setProvider] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<number | null>(targetAttempt);
  const targetRef = useRef<HTMLDivElement | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      let query = supabase
        .from('payment_attempt_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      const since = sinceISO(period);
      if (since) query = query.gte('created_at', since);
      const { data, error } = await query;
      if (error) throw error;
      setRows((data as PaymentAttemptRow[]) || []);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Impossible de charger le journal des paiements');
    } finally {
      setLoading(false);
    }
    // Mini-graphique : 7 derniers jours, indépendant du filtre de période.
    try {
      const weekSince = new Date(Date.now() - 7 * 86400000).toISOString();
      const { data: week } = await supabase
        .from('payment_attempt_logs')
        .select('operator')
        .gte('created_at', weekSince)
        .in('status', FAILED_STATES)
        .limit(1000);
      setWeekRows((week as { operator: string | null }[]) || []);
    } catch {
      setWeekRows([]);
    }
  }, [period]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    const timer = window.setInterval(() => void fetchRows(), 60000);
    return () => window.clearInterval(timer);
  }, [fetchRows]);

  useEffect(() => {
    if (!targetAttempt || rows.length === 0) return;
    if (rows.some((r) => r.id === targetAttempt)) {
      setExpanded(targetAttempt);
      window.setTimeout(() => {
        targetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 120);
    }
  }, [rows, targetAttempt]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (provider !== 'all' && row.provider !== provider) return false;
      if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      if (q) {
        const haystack = `${row.provider} ${row.operator || ''} ${row.country || ''} ${row.error_message || ''} ${row.reference || ''} ${row.raw_response || ''} ${row.endpoint || ''} ${row.user_label || ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [rows, provider, statusFilter, search]);

  const stats = useMemo(() => {
    const now = Date.now();
    const failures = rows.filter((r) => isFailure(r.status));
    const last24h = failures.filter((r) => now - new Date(r.created_at).getTime() < 86400000);
    const operators = new Map<string, number>();
    failures.forEach((r) => {
      const key = r.operator || 'inconnu';
      operators.set(key, (operators.get(key) || 0) + 1);
    });
    const topOperator = Array.from(operators.entries()).sort((a, b) => b[1] - a[1])[0];
    // Montants envoyés en zone CFA (XOF Ouest, XAF Centre : parité fixe).
    const gatewayTotal = failures
      .filter((r) => ['XOF', 'XAF'].includes((r.gateway_currency || '').toUpperCase()))
      .reduce((sum, r) => sum + (Number(r.gateway_amount) || 0), 0);
    const providers = new Set(failures.map((r) => r.provider));
    const initiated = rows.filter((r) => r.status === 'initiated').length;
    const confirmed = rows.filter((r) => r.status === 'confirmed').length;
    return {
      total: failures.length,
      last24h: last24h.length,
      topOperator: topOperator ? `${topOperator[0]} (${topOperator[1]})` : '—',
      gatewayTotal,
      providers: providers.size,
      initiated,
      confirmed,
    };
  }, [rows]);

  const providers = useMemo(() => Array.from(new Set(rows.map((r) => r.provider))), [rows]);

  // 📊 Échecs par opérateur sur les 7 derniers jours (mini-graphique).
  const operatorChart = useMemo(() => {
    const map = new Map<string, number>();
    weekRows.forEach((r) => {
      const key = r.operator ? String(r.operator) : 'Inconnu';
      map.set(key, (map.get(key) || 0) + 1);
    });
    const list = Array.from(map.entries())
      .map(([operator, total]) => ({ operator, total }))
      .sort((a, b) => b.total - a.total);
    const max = list[0]?.total || 1;
    return { list: list.slice(0, 6), max, total: list.reduce((s, i) => s + i.total, 0) };
  }, [weekRows]);

  const exportCsv = () => {
    if (filtered.length === 0) return;
    const headers = [
      'Date', 'Fournisseur', 'Parcours', 'Statut', 'Montant', 'Devise',
      'Montant_envoye', 'Devise_envoyee', 'Operateur', 'Pays', 'Telephone_masque',
      'Statut_HTTP', 'Endpoint', 'Reference', 'Abonnement', 'Commande',
      'Code_erreur', 'Message_erreur', 'Reponse_brute', 'Utilisateur',
    ];
    const csvRows = filtered.map((r) => [
      new Date(r.created_at).toISOString(),
      PROVIDER_LABELS[r.provider] || r.provider,
      FLOW_LABELS[r.flow || 'other'] || r.flow || '',
      STATUS_LABELS[r.status] || r.status,
      r.amount != null ? String(r.amount) : '',
      r.currency || '',
      r.gateway_amount != null ? String(r.gateway_amount) : '',
      r.gateway_currency || '',
      r.operator || '',
      r.country || '',
      r.phone || '',
      r.http_status != null ? String(r.http_status) : '',
      r.endpoint || '',
      r.reference || '',
      r.subscription_id != null ? String(r.subscription_id) : '',
      r.order_id != null ? String(r.order_id) : '',
      r.error_code || '',
      r.error_message || '',
      r.raw_response || '',
      r.user_label || r.user_id || '',
    ]);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    downloadCSV(`zifek-paiements-echoues-${stamp}.csv`, headers, csvRows);
  };

  return (
    <div>
      <div className="mb-5 rounded-lg border border-secondary-200/70 bg-secondary-50/60 px-4 py-3 flex items-start gap-3">
        <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-800 flex items-center justify-center flex-shrink-0">
          <i className="ri-secure-payment-line text-sm"></i>
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-foreground-900">Chaque tentative FeexPay est tracée, réussie ou non</p>
          <p className="text-[11px] text-foreground-600 mt-0.5">
            Montant, opérateur, devise réellement envoyée, statut HTTP et réponse brute du fournisseur.
            Les demandes envoyées ({stats.initiated}) et les paiements confirmés ({stats.confirmed}) apparaissent
            aussi : impossible de perdre une transaction sans le voir ici.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        {[
          { icon: 'ri-error-warning-line', label: 'Échecs sur la période', value: stats.total.toLocaleString(), sub: `${stats.providers} fournisseur(s) concerné(s)`, color: 'bg-red-50 text-red-600' },
          { icon: 'ri-time-line', label: 'Dernières 24 h', value: stats.last24h.toLocaleString(), sub: 'Tentatives refusées', color: 'bg-amber-50 text-amber-700' },
          { icon: 'ri-smartphone-line', label: 'Opérateur le plus touché', value: stats.topOperator, sub: 'Nombre de refus', color: 'bg-primary-100 text-primary-700' },
          { icon: 'ri-money-dollar-circle-line', label: 'Montant concerné (XOF/XAF)', value: stats.gatewayTotal.toLocaleString('fr-FR'), sub: 'Somme envoyée à la passerelle', color: 'bg-accent-100 text-accent-700' },
        ].map((card) => (
          <div key={card.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <div className={`w-8 h-8 rounded-lg ${card.color} flex items-center justify-center mb-2`}>
              <i className={`${card.icon} text-sm`}></i>
            </div>
            <div className="text-lg font-bold font-heading text-foreground-950 truncate">{card.value}</div>
            <div className="text-xs text-foreground-500">{card.label}</div>
            <div className="text-[11px] text-foreground-400 mt-0.5">{card.sub}</div>
          </div>
        ))}
      </div>

      {/* Mini-graphique : échecs par opérateur sur 7 jours */}
      <div className="mb-6 rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="text-xs font-semibold text-foreground-700 flex items-center gap-2">
            <i className="ri-bar-chart-horizontal-line text-primary-500"></i>
            Échecs par opérateur — 7 derniers jours
          </h3>
          <span className="text-[10px] px-2 py-1 rounded-full bg-secondary-100 text-secondary-900 font-medium whitespace-nowrap">
            {operatorChart.total} échec(s) sur 7 jours
          </span>
        </div>
        {operatorChart.list.length === 0 ? (
          <p className="text-[11px] text-foreground-400 py-3">Aucun échec de paiement enregistré sur les 7 derniers jours.</p>
        ) : (
          <div className="space-y-2.5">
            {operatorChart.list.map((item) => (
              <div key={item.operator} className="flex items-center gap-3">
                <span className="w-24 md:w-28 text-[11px] font-medium text-foreground-700 truncate whitespace-nowrap" title={item.operator}>
                  {item.operator}
                </span>
                <div className="flex-1 h-2.5 rounded-full bg-background-200/70 overflow-hidden min-w-0">
                  <div
                    className="h-full rounded-full bg-primary-500"
                    style={{ width: `${Math.max(6, Math.round((item.total / operatorChart.max) * 100))}%` }}
                  ></div>
                </div>
                <span className="text-[11px] font-semibold text-foreground-800 w-7 text-right">{item.total}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
          {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                period === p ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
          {[{ key: 'all', label: 'Tous' }, ...providers.map((p) => ({ key: p, label: PROVIDER_LABELS[p] || p }))].map((f) => (
            <button
              key={f.key}
              onClick={() => setProvider(f.key)}
              className={`px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                provider === f.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
          {[
            { key: 'all', label: 'Tout statut' },
            { key: 'failed', label: 'Échecs' },
            { key: 'declined', label: 'Refus' },
            { key: 'initiated', label: 'Envoyées' },
            { key: 'confirmed', label: 'Confirmées' },
            { key: 'unmatched', label: 'Non appariées' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                statusFilter === f.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="relative flex-1 min-w-0">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une erreur, un opérateur, une référence…"
            className="w-full h-9 pl-9 pr-3 rounded-full border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
          />
        </div>

        <button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          className="flex items-center gap-1.5 h-9 px-3 rounded-full border border-background-200/70 bg-background-50 text-[11px] font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
        >
          <i className="ri-file-excel-2-line"></i>
          Exporter CSV
          <span className="text-foreground-400">({filtered.length})</span>
        </button>

        <button
          onClick={() => void fetchRows()}
          className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200/70 bg-background-50 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
          title="Rafraîchir"
        >
          <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`}></i>
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : loadError ? (
        <div className="flex flex-col items-center justify-center py-16 border border-background-200/70 rounded-lg bg-background-50">
          <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
          <p className="text-sm text-foreground-600 mb-3">{loadError}</p>
          <button onClick={() => void fetchRows()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-medium cursor-pointer hover:bg-primary-600 transition-colors">
            Réessayer
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-background-200/70 rounded-lg bg-background-50">
          <i className="ri-shield-check-line text-4xl text-accent-400 mb-3 block"></i>
          <p className="text-sm font-medium text-foreground-700">Aucun paiement échoué sur cette période</p>
          <p className="text-xs text-foreground-500 mt-1">Les prochains refus FeexPay (ou autre moyen) apparaîtront ici avec la réponse brute du fournisseur.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((row) => {
            const isOpen = expanded === row.id;
            const isTarget = targetAttempt === row.id;
            const retryPlan = typeof row.request_payload?.plan === 'string' ? row.request_payload.plan : '';
            const retryMethod = row.provider || 'feexpay';
            const retryHref = `/dashboard/abonnement/choisir?retry=1${retryPlan ? `&plan=${encodeURIComponent(retryPlan)}` : ''}&method=${encodeURIComponent(retryMethod)}`;
            return (
              <div
                key={row.id}
                ref={isTarget ? targetRef : undefined}
                className={`rounded-lg border bg-background-50 overflow-hidden transition-colors ${isTarget ? 'border-amber-300 ring-1 ring-amber-200' : 'border-background-200/70'}`}
              >
                <button
                  onClick={() => setExpanded(isOpen ? null : row.id)}
                  className="w-full text-left px-4 py-3 flex items-start gap-3 cursor-pointer"
                >
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${STATUS_STYLE[row.status] || 'bg-background-100 text-foreground-600'}`}>
                    <i className={`${STATUS_ICONS[row.status] || 'ri-information-line'} text-sm`}></i>
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-700 font-medium whitespace-nowrap">
                        {PROVIDER_LABELS[row.provider] || row.provider}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-800 font-medium whitespace-nowrap">
                        {FLOW_LABELS[row.flow || 'other'] || row.flow}
                      </span>
                      {row.operator && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap">
                          {row.operator}
                        </span>
                      )}
                      {row.http_status != null && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-background-200/70 text-foreground-600 font-mono whitespace-nowrap">
                          HTTP {row.http_status}
                        </span>
                      )}
                      <span className="text-[10px] text-foreground-400 whitespace-nowrap">{relative(row.created_at)}</span>
                    </div>
                    <p className="text-xs text-foreground-800 break-words line-clamp-2">{row.error_message || STATUS_LABELS[row.status] || 'Tentative de paiement'}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[10px] text-foreground-400">
                      <span className="font-semibold text-foreground-700">
                        {amountLabel(row.amount, row.currency)}
                        {row.gateway_amount != null && (row.gateway_currency || '').toUpperCase() !== (row.currency || '').toUpperCase() && (
                          <span className="text-foreground-400 font-normal"> → {amountLabel(row.gateway_amount, row.gateway_currency)}</span>
                        )}
                      </span>
                      {row.country && <span>pays : {row.country}</span>}
                      {row.phone && <span className="font-mono">{row.phone}</span>}
                      {row.reference && <span className="truncate max-w-[220px] font-mono">{row.reference}</span>}
                    </div>
                  </div>
                  <i className={`${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400 flex-shrink-0`}></i>
                </button>

                {isOpen && (
                  <div className="border-t border-background-200/70 bg-background-100/30 px-4 py-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Montant demandé</p>
                        <p className="text-xs text-foreground-800">{amountLabel(row.amount, row.currency)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Montant envoyé</p>
                        <p className="text-xs text-foreground-800">{amountLabel(row.gateway_amount, row.gateway_currency)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Date exacte</p>
                        <p className="text-xs text-foreground-800">{shortDate(row.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Opérateur / méthode</p>
                        <p className="text-xs text-foreground-800">{row.operator || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Utilisateur</p>
                        <p className="text-xs text-foreground-800 break-all">{row.user_label || row.user_id || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Code erreur</p>
                        <p className="text-xs text-foreground-800 font-mono">{row.error_code || '—'}</p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Endpoint appelé</p>
                        <p className="text-[11px] text-foreground-600 font-mono break-all">{row.endpoint || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Référence</p>
                        <p className="text-[11px] text-foreground-600 font-mono break-all">{row.reference || '—'}</p>
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Réponse brute du fournisseur</p>
                      <pre className="whitespace-pre-wrap break-words rounded-md bg-background-50 border border-background-200/70 p-3 text-[11px] text-foreground-700 font-mono max-h-64 overflow-auto">
{row.raw_response || '(aucune réponse enregistrée)'}
                      </pre>
                    </div>

                    {row.request_payload && (
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Requête envoyée (sans secret)</p>
                        <pre className="whitespace-pre-wrap break-words rounded-md bg-background-50 border border-background-200/70 p-3 text-[11px] text-foreground-700 font-mono max-h-48 overflow-auto">
{JSON.stringify(row.request_payload, null, 2)}
                        </pre>
                      </div>
                    )}

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {row.flow === 'subscription' ? (
                        <Link
                          to={retryHref}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap no-underline"
                          title="Rouvrir le tunnel d'abonnement avec la même formule pour réessayer le paiement"
                        >
                          <i className="ri-restart-line"></i>
                          Relancer le paiement
                        </Link>
                      ) : (
                        <Link
                          to="/dashboard/orders/new"
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap no-underline"
                          title="Recréer la commande boutique et retenter le paiement"
                        >
                          <i className="ri-restart-line"></i>
                          Relancer la commande
                        </Link>
                      )}
                      <Link
                        to="/superadmin/payments"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium bg-background-50 border border-background-200/70 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap no-underline"
                      >
                        <i className="ri-secure-payment-line"></i>
                        Paiements plateforme
                      </Link>
                      <span className="text-[10px] text-foreground-400">
                        Une nouvelle tentative crée une ligne distincte dans ce journal.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}